
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, FormControlLabel, styled, Switch, Typography, useTheme } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { HotelCheckOptionInstructionDTO } from '@bandido/shared/dtos';

import { langConfig } from '@frontend/features/common/constants/languages';


const useValidationSchema = () => {
  const { t } = useTranslation();
  return yup.object().shape({
    text: yup
      .string()
      .required(t('common.form.required')),
    isSpecial: yup
      .boolean()
      .required(t('common.form.required')),
  });
};

interface Props {
  checkOptionInstruction: HotelCheckOptionInstructionDTO;
  onSave: (updatedInstruction: HotelCheckOptionInstructionDTO) => void;
  onCancel: () => void;
}

const EditorWrapperBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primaryAlternative.darker,
  marginTop: theme.spacing(3),
  padding: theme.spacing(1),
}));

const Image = styled('img')(({ theme }) => ({
  borderRadius: theme.spacing(1),
  width: theme.spacing(2),
  height: theme.spacing(2),
  marginRight: theme.spacing(1),
}));

const EditorBox = styled(Box)(() => ({
  width: '100%',
  backgroundColor: '#ede7f6',
  '&& .ck-editor__main': {
    border: 'none',
  },
  '.ck-editor__editable, .ck.ck-toolbar': {
    backgroundColor: 'transparent !important',
  },
}));


export const CheckOptionInstructionsEditor: FC<Props> = ({ checkOptionInstruction, onSave, onCancel }) => {
  const { t } = useTranslation();
  const {text, language, isSpecial } = checkOptionInstruction;
  const validationSchema = useValidationSchema();
  const theme = useTheme();

  const { handleSubmit, control } = useForm({
    defaultValues: {
      text,
      isSpecial,
    },
    resolver: yupResolver(validationSchema),
  });
  
  const onSubmit = (data: { text: string, isSpecial: boolean}) => {
    const { isSpecial, text } = data;
    onSave({
      ...checkOptionInstruction,
      text,
      isSpecial
    });
  };

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      width = "100%"
      onSubmit={handleSubmit(onSubmit)}
      gap={theme.spacing(2)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        gap={theme.spacing(2)}
      >
        
        <Typography variant="h6">
          {t('hotels.contentManager.detail.checkOptions.editor.instructions.edit')}
        </Typography>
        <Box
          display="flex"
          gap={theme.spacing(2)}
        >
          <Button
            color='primary'
            variant='outlined'
            size='small'
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color='primary'
            variant='contained'
            size='small'
            type='submit'
          >
            {t('common.form.save')}
          </Button>
        </Box>
      </Box>

      <EditorWrapperBox key={language}>
        <Box
          display="flex"
          justifyContent="space-between"
          pb={theme.spacing(2)}
        >
          <Typography
            variant="body2"
            display="flex"
            alignItems="center"
            color={theme.palette.primary.main}
          >
            <Image src={langConfig[language].flag} />
            {t(langConfig[language].text)}
          </Typography>
          <FormControlLabel
            control={(
              <Controller
                name="isSpecial"
                control={control}
                render={({ field }) => (
                  <Switch { ...field } />
                )}
              />
            )}
            label={t('hotels.contentManager.detail.checkOptions.editor.instructions.isSpecial')}
          />
          
        </Box>
        <Box display="flex" flexWrap="wrap">
          <Box
            width="100%"
            display="flex"
            flexWrap="wrap"
            gap={theme.spacing(2)}
            mt={theme.spacing(2)}
          >
            <Controller
              name={'text'}
              control={control}
              render={({ field }) => (
                <EditorBox>
                  <CKEditor
                    editor={ClassicEditor as any}
                    data={field.value}
                    onChange={(_: any, editor: any) => {
                      const data = editor.getData();
                      field.onChange(data);
                    }}
                  />
                </EditorBox>
              )}
            />
          </Box>
        </Box>
      </EditorWrapperBox>
    </Box>
  );
};

